/**
 * A walkthrough of the repair, on the customer's copy.
 *
 * Jonathon, 2026-09-04: "i want these videos to populate on the appropriate
 * repair estimate ... right underneath the photo section."
 *
 * Built from the design handoff that came with the footage, with two things
 * changed deliberately, both because that handoff was written for a page it
 * could not see:
 *
 * - **No half-speed playback setting.** The prototype slowed the video down in
 *   the player. Ours is rendered at its finished speed, so what a rep previews
 *   is the file, and no browser that ignores `playbackRate` can show a customer
 *   a video running twice as fast as it was cut.
 * - **No resume position.** A proposal is read once, by somebody who has never
 *   seen it. Dropping a customer into the middle of a video because they
 *   glanced at the page yesterday is worse than starting again.
 *
 * Everything else is the handoff's: one large play control, and the poster
 * before the customer presses it.
 *
 * Jonathon, 2026-09-13: "can you make the video loop without sound?" — so once
 * it is running it runs silently, on repeat, until the customer stops it
 * (ADR 0143). Two consequences worth knowing before editing this file:
 *
 * - **There is no end, so there is no Watch again.** `ended` never fires on a
 *   looping element. The control that used to replace the final frame is gone
 *   rather than left in as a branch nothing can reach.
 * - **The mute is the element's, not the file's.** Every walkthrough we have
 *   is rendered without an audio track, but a future one will not necessarily
 *   be, and "without sound" is the instruction. `controls` stays on while it
 *   plays, so a customer who wants sound from a video that has some can turn
 *   it on.
 *
 * Muting also retires the Safari retry this player used to carry. That retry
 * existed because Safari refuses a `play()` **with sound** on a page the
 * reader has not interacted with; a muted element is not what it refuses. What
 * is left is the honest fallback: if the browser will not play it at all, put
 * the poster back rather than sit on a frozen frame.
 */

import { useRef, useState } from "react";

export interface PresentedVideo {
  url: string;
  posterUrl: string | null;
  title: string;
  caption: string | null;
}

type Phase = "idle" | "playing";

export function ProductVideoPlayer({ video }: { video: PresentedVideo }) {
  const [phase, setPhase] = useState<Phase>("idle");
  const [failed, setFailed] = useState(false);
  const element = useRef<HTMLVideoElement>(null);

  /** Autoplay is off by design — the customer clicks. */
  async function start() {
    const media = element.current;
    if (!media) return;
    // React has historically set `muted` as an attribute and not as the
    // property the media stack actually reads, so set it here as well. Belt
    // and braces on the one thing the customer would notice going wrong.
    media.muted = true;
    setPhase("playing");
    try {
      await media.play();
    } catch {
      // Nothing left to try. Put the poster back rather than sit on a frozen
      // frame the customer cannot restart.
      setPhase("idle");
    }
  }

  // A video that will not load is left out entirely. A broken player on a
  // proposal is worse than a proposal with one fewer section on it.
  if (failed) return null;

  return (
    <figure className="pdoc-video">
      <div className="frame">
        <video
          ref={element}
          src={video.url}
          poster={video.posterUrl ?? undefined}
          preload="metadata"
          playsInline
          loop
          muted
          controls={phase === "playing"}
          onError={() => setFailed(true)}
        />

        {phase !== "playing" && (
          <button
            type="button"
            className="play"
            // Fire and forget: start() falls back to the poster on failure.
            onClick={() => void start()}
            aria-label={`Play ${video.title}`}
          >
            <span className="disc" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="34" height="34">
                <path d="M8 5v14l11-7z" fill="currentColor" />
              </svg>
            </span>
          </button>
        )}
      </div>

      <figcaption>
        <strong>{video.title}</strong>
        {video.caption ? <span>{video.caption}</span> : null}
      </figcaption>
    </figure>
  );
}
